import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { mangaList } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'love');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = useMemo(() => categories.find((c) => c.slug === activeCategory), [activeCategory]);
  const filteredManga = useMemo(
    () => mangaList.filter((m) => m.category === activeCategory),
    [activeCategory],
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '漫画分类'} - 韩漫画网`,
    description: currentCategory?.description || '浏览韩漫画网的漫画分类',
    url: `https://hanmanhua.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'https://hanmanhua.com/',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanhua.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '分类' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory?.name || '漫画分类'}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              {currentCategory?.description || '浏览韩漫画网的免费漫画分类'}
            </p>
          </section>

          {/* Category Tabs */}
          <section className="bg-background-100 border-y border-background-200/70 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto">
              <div className="flex gap-1 py-2 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-500 hover:bg-background-50'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Manga Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.slug} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </section>

          {/* Related Categories */}
          <section className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                更多分类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {categories
                  .filter((c) => c.slug !== activeCategory)
                  .slice(0, 4)
                  .map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className={cat.icon}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</p>
                        <p className="text-xs text-foreground-500">{cat.count}部作品</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}